import axios from 'axios';
import { notifySaving, notifySaved, notifyError } from './saveStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

// ── Save status interceptors ────────────────────────────────────────────────
// Solo mutation HTTP (POST/PUT/PATCH/DELETE) influenzano l'indicatore "Salvato".
// Le request taggate con `config.silent = true` sono ignorate (utile per
// operazioni di sola lettura mascherate da POST, p.es. coach chat).
const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

function isMutation(cfg) {
  if (!cfg || cfg.silent) return false;
  const m = (cfg.method || 'get').toLowerCase();
  return MUTATION_METHODS.has(m);
}

api.interceptors.request.use((cfg) => {
  if (isMutation(cfg)) {
    cfg._qpTrackSave = true;
    notifySaving();
  }
  return cfg;
});

api.interceptors.response.use(
  (res) => {
    if (res.config?._qpTrackSave) notifySaved();
    return res;
  },
  (err) => {
    if (err?.config?._qpTrackSave) {
      const msg = err?.response?.data?.detail || err?.message || 'errore';
      notifyError(msg);
    }
    return Promise.reject(err);
  }
);

// ── Lightweight TTL cache for read-heavy endpoints ──────────────────────────
// Uso: `memoCached('stats', 60_000, () => api.get(...))`
// - Stessa key → ritorna la promise condivisa entro TTL ms
// - In-flight dedupe: se più componenti chiamano fetcher() in parallelo,
//   condividono la STESSA pending promise (non parte 1 GET per mount).
// - `invalidateCache(prefix)` butta tutte le entry con quel prefisso.
const _cache = new Map(); // key → { ts, value, pending }

function memoCached(key, ttlMs, fetcher) {
  const now = Date.now();
  const hit = _cache.get(key);
  if (hit) {
    if (hit.pending) return hit.pending; // in-flight: riusa la promise
    if (now - hit.ts < ttlMs) return Promise.resolve(hit.value);
  }
  const pending = fetcher()
    .then((value) => {
      _cache.set(key, { ts: Date.now(), value });
      return value;
    })
    .catch((err) => {
      _cache.delete(key); // non bloccare retry su errore
      throw err;
    });
  _cache.set(key, { ts: now, value: hit?.value, pending });
  return pending;
}

export function invalidateCache(prefix) {
  if (!prefix) {
    _cache.clear();
    return;
  }
  for (const k of Array.from(_cache.keys())) {
    if (k.startsWith(prefix)) _cache.delete(k);
  }
}

// Esposto per debug/test (non strettamente API)
export const __cacheSize = () => _cache.size;
export const fetchDashboard = (anno) =>
  memoCached(`dashboard:${anno || 'current'}`, 60_000, () =>
    api.get('/dashboard', { params: anno ? { anno } : {} }).then((r) => r.data),
  );

export const updateAnno = (id, payload) =>
  api.put(`/anno/${id}`, payload).then((r) => r.data);

export const seedDemo = () => api.post('/seed-demo').then((r) => r.data);

export const seedReal2026 = () => api.post('/seed-real-2026').then((r) => r.data);

export const fetchStatePrint = () => api.get('/state/print').then((r) => r.data);

export const saveQuarterMeta = ({ quarter, focus, rischi }) =>
  api.post('/quarter-meta', { quarter, focus, rischi }).then((r) => r.data);

export const fetchState = (anno) =>
  api.get('/state', { params: anno ? { anno } : {} }).then((r) => r.data);

export const fetchStateMese = (mese) =>
  api.get('/state/mese', { params: mese ? { mese } : {} }).then((r) => r.data);

export const fetchStateTrimestre = (quarter) =>
  api.get('/state/trimestre', { params: quarter ? { quarter } : {} }).then((r) => r.data);

export const fetchStateSettimana = (settimana) =>
  api.get('/state/settimana', { params: settimana ? { settimana } : {} }).then((r) => r.data);

export const sendCoach = ({ momento, stato, messaggi }) =>
  api.post('/coach', { momento, stato, messaggi }, { silent: true }).then((r) => r.data);

/**
 * Variante streaming di sendCoach: ascolta il flusso SSE da POST /api/coach/stream
 * e chiama:
 *   - onDelta(text) per ogni token di testo che arriva
 *   - onDone({ messaggio, operazioni, suggerimenti }) all'evento "done"
 *
 * Ritorna una Promise che si risolve quando lo stream è completo, o si rigetta
 * in caso di errore di rete / parsing / 4xx-5xx. Il chiamante è responsabile del
 * fallback alla `sendCoach` non-streaming.
 *
 * NB: usa fetch() perché EventSource non supporta header auth né POST.
 */
export async function sendCoachStream({ momento, stato, messaggi, onDelta, onDone, signal }) {
  // Import dinamico per evitare ciclo: auth.jsx importa già api.js.
  const { getCurrentToken } = await import('./auth');
  const token = getCurrentToken();
  if (!token) throw new Error('No auth token (not signed in)');

  const res = await fetch(`${API}/coach/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ momento, stato, messaggi }),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`coach/stream HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalPayload = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE: eventi separati da \n\n, ogni riga "data: <json>"
    let sepIdx;
    while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);
      for (const line of rawEvent.split('\n')) {
        if (!line.startsWith('data:')) continue;
        const json = line.slice(5).trim();
        if (!json) continue;
        let evt;
        try { evt = JSON.parse(json); } catch { continue; }
        if (evt.type === 'text' && typeof evt.delta === 'string') {
          if (onDelta) onDelta(evt.delta);
        } else if (evt.type === 'done') {
          finalPayload = {
            messaggio: evt.messaggio || '',
            operazioni: Array.isArray(evt.operazioni) ? evt.operazioni : [],
            suggerimenti: Array.isArray(evt.suggerimenti) ? evt.suggerimenti : [],
          };
          if (onDone) onDone(finalPayload);
        } else if (evt.type === 'error') {
          throw new Error(evt.message || 'stream error');
        }
      }
    }
  }
  return finalPayload || { messaggio: '', operazioni: [], suggerimenti: [] };
}

export const applyOperations = (operazioni) =>
  api.post('/operations/apply', { operazioni }).then((r) => {
    invalidateCache('stats:');
    invalidateCache('dashboard:');
    return r.data;
  });

export const repairAzioniSettimana = () =>
  api.post('/admin/repair-azioni-settimana').then((r) => r.data);

export const previewDecomposition = ({ baseline, target_committed, anno, ripartizione = 'lineare', granularita = 'quarter', pesi_custom = null }) =>
  api.post('/decomposition/preview', { baseline, target_committed, anno, ripartizione, granularita, pesi_custom }, { silent: true }).then((r) => r.data);

export const replanTargetPeriodo = ({ key_result_id, ripartizione = 'lineare' }) =>
  api.post('/decomposition/replan', { key_result_id, ripartizione }).then((r) => r.data);

/**
 * Duplica le azioni non chiuse della settimana `settimana` sulla settimana
 * destinazione (default = settimana successiva).
 * @returns {Promise<{ok, count, from, to, created}>}
 */
export const copyOpenActions = (settimana, { to } = {}) =>
  api.post(`/weekly/${settimana}/copy-open${to ? `?to=${to}` : ''}`).then((r) => r.data);

// ── Drill-down nav ──
export const fetchObiettivo = (id) =>
  api.get(`/obiettivo/${id}`).then((r) => r.data);

export const fetchKrRevisioni = (krId) =>
  api.get(`/kr/${krId}/revisioni`, { silent: true }).then((r) => r.data);

export const fetchPeriodo = (granularita, etichetta, kr) =>
  api.get(`/periodo/${granularita}/${etichetta}`, { params: kr ? { kr } : {} }).then((r) => r.data);

export const fetchOggi = () => api.get('/oggi').then((r) => r.data);

export const fetchLlmUsage = ({ scope = 'me', days = 30 } = {}) =>
  api.get('/llm-usage', { params: { scope, days } }).then((r) => r.data);

export const fetchPersonalStats = () =>
  memoCached('stats:personal', 60_000, () =>
    api.get('/stats/personal', { silent: true }).then((r) => r.data),
  );

export const fetchClosedWeek = () =>
  memoCached('stats:closed-week', 30_000, () =>
    api.get('/stats/closed-week', { silent: true }).then((r) => r.data),
  );

export const fetchClosedWeeks = (n = 8, area = null) => {
  const params = new URLSearchParams({ n: String(n) });
  if (area) params.set('area', area);
  const key = `stats:closed-weeks:${n}:${area || 'all'}`;
  return memoCached(key, 60_000, () =>
    api.get(`/stats/closed-weeks?${params.toString()}`, { silent: true }).then((r) => r.data),
  );
};

export const saveCoachLog = ({ momento, messaggi, operazioni, esito }) =>
  api.post('/coach/log', { momento, messaggi, operazioni, esito }, { silent: true }).then((r) => r.data);

export const fetchCoachLogs = () =>
  api.get('/coach/log', { silent: true }).then((r) => r.data);

export const fetchCoachLogsRecent = (limit = 12) =>
  api.get(`/coach/log/recent?limit=${limit}`, { silent: true }).then((r) => r.data);

export const fetchCoachLog = (id) =>
  api.get(`/coach/log/${id}`, { silent: true }).then((r) => r.data);

// ── Google Calendar ──
export const fetchCalendarConnection = () =>
  api.get('/calendar/connection', { silent: true }).then((r) => r.data);
export const startCalendarOAuth = () =>
  api.get('/oauth/calendar/start', { silent: true }).then((r) => r.data);
export const disconnectCalendar = () =>
  api.post('/oauth/calendar/disconnect').then((r) => r.data);
export const fetchGcalLastSync = () =>
  api.get('/gcal/last-sync', { silent: true }).then((r) => r.data);
export const triggerGcalSyncMe = () =>
  api.post('/gcal/sync-me').then((r) => r.data);

// ── Toggle done ──
// I toggle MIT/Azione cambiano i counter del dashboard e delle stats:
// invalidiamo il cache così la prossima visita ricarica fresh.
export const toggleMit = (id, fatto) =>
  api.patch(`/mit/${id}/toggle`, { fatto }).then((r) => {
    invalidateCache('stats:');
    invalidateCache('dashboard:');
    return r.data;
  });

export const toggleAzione = (id, fatto) =>
  api.patch(`/azione/${id}/toggle`, { fatto }).then((r) => {
    invalidateCache('stats:');
    invalidateCache('dashboard:');
    return r.data;
  });

export const toggleTargetPeriodo = (id, raggiunto) =>
  api.patch(`/target-periodo/${id}/toggle`, { raggiunto }).then((r) => {
    invalidateCache('dashboard:');
    return r.data;
  });

export const patchKrStato = (id, stato) =>
  api.patch(`/key-result/${id}/stato`, { stato }).then((r) => {
    invalidateCache('stats:');
    invalidateCache('dashboard:');
    return r.data;
  });

export const fetchCalendario = (year, month) =>
  api.get(`/calendario/${year}/${month}`).then((r) => r.data);

export const fetchTimeline = (anno) =>
  api.get('/timeline', { params: anno ? { anno } : {} }).then((r) => r.data);

export const fetchAvvisi = () => api.get('/avvisi').then((r) => r.data);

export const dismissAvviso = (kind, periodo = '') =>
  api.post('/avvisi/dismiss', { kind, periodo }).then((r) => r.data);

// ─── Ricorrenze ──────────────────────────────────────────────────────────
export const setRecurrenceOverride = (payload) =>
  api.post('/recurrence/override', payload).then((r) => r.data);

export const convertToSeries = (parent_kind, parent_id, recurrence_rule, recurrence_end = null) =>
  api.post('/recurrence/series/convert', { parent_kind, parent_id, recurrence_rule, recurrence_end }).then((r) => r.data);

export const clearSeries = (parent_kind, parent_id) =>
  api.post('/recurrence/series/clear', { parent_kind, parent_id }).then((r) => r.data);

export const deleteSeries = (parent_kind, parent_id) =>
  api.post('/recurrence/series/delete', { parent_kind, parent_id }).then((r) => r.data);

export const fetchHeatmap = (year) =>
  api.get(`/heatmap/${year}`).then((r) => r.data);

export const fetchFraseDelGiorno = () =>
  api.get('/frase-del-giorno', { silent: true }).then((r) => r.data);

export const fetchRecap = (year) =>
  api.get(`/recap/${year}`, { silent: true }).then((r) => r.data);

export const sendDigestMeWeekly = () =>
  api.post('/email/me/weekly').then((r) => r.data);

export const sendDigestMeMonthly = () =>
  api.post('/email/me/monthly').then((r) => r.data);

// ── Quick add (MIT / Azione) ──
export const createMit = (payload) =>
  api.post('/mit', payload).then((r) => { invalidateCache('stats:'); invalidateCache('dashboard:'); return r.data; });
export const createAzione = (payload) =>
  api.post('/azione', payload).then((r) => { invalidateCache('stats:'); invalidateCache('dashboard:'); return r.data; });
export const deleteMit = (id) =>
  api.delete(`/mit/${id}`).then((r) => { invalidateCache('stats:'); invalidateCache('dashboard:'); return r.data; });
export const deleteAzione = (id) =>
  api.delete(`/azione/${id}`).then((r) => { invalidateCache('stats:'); invalidateCache('dashboard:'); return r.data; });

// ── Promuovi azione → MIT ──
export const promoteAzioneToMit = (azioneId, data) =>
  api.post(`/azione/${azioneId}/promote-mit`, { data }).then((r) => r.data);

// ── Retrocedi MIT → azione ──
// mantieni_data=true (default) copia MIT.data in azione.data (pinned)
// settimana opzionale: se null viene derivata dal giorno della MIT lato backend
export const demoteMitToAzione = (mitId, opts = {}) =>
  api.post(`/mit/${mitId}/demote-azione`, {
    settimana: opts.settimana || null,
    mantieni_data: opts.mantieni_data !== false,
  }).then((r) => { invalidateCache('stats:'); invalidateCache('dashboard:'); return r.data; });

// ── Coach smart suggest MIT ──
// Body: { stato: { azioni:[...], kr:[...] }, giorno?, settimana? }
export const suggerisciMit = (body) =>
  api.post('/coach/suggerisci-mit', body, { silent: true }).then((r) => r.data);

// ── Aree utente (CRUD) ──
// Tutte le mutation aree sono `silent` per evitare di triggerare il
// SaveIndicator globale (la UI gestisce i toast localmente).
export const fetchAree = () => api.get('/aree', { silent: true }).then((r) => r.data);
export const createArea = (payload) => api.post('/aree', payload, { silent: true }).then((r) => r.data);
export const updateArea = (keyOrId, payload) =>
  api.patch(`/aree/${encodeURIComponent(keyOrId)}`, payload, { silent: true }).then((r) => r.data);
export const deleteArea = (keyOrId) =>
  api.delete(`/aree/${encodeURIComponent(keyOrId)}`, { silent: true }).then((r) => r.data);

export const searchAll = (q) =>
  api.get('/search', { params: { q } }).then((r) => r.data);

// ── Export dati utente ──
export const exportJsonUrl = `${API}/export/json`;
export const exportCsvUrl = `${API}/export/csv`;
export const downloadExportJson = () =>
  api.get('/export/json', { responseType: 'blob', silent: true }).then((r) => ({ blob: r.data, headers: r.headers }));
export const downloadExportCsv = () =>
  api.get('/export/csv', { responseType: 'blob', silent: true }).then((r) => ({ blob: r.data, headers: r.headers }));
